import React, { useMemo } from "react";
import {
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
  ReferenceArea,
  CartesianGrid,
} from "recharts";
import type { AgilePrice, BatteryOperation } from "@/lib/api";

type Props = {
  prices: AgilePrice[];
  operations?: BatteryOperation[];
};

const CHEAP_THRESHOLD_P = 12;

function floorToHalfHour(d: Date): Date {
  const out = new Date(d);
  out.setSeconds(0, 0);
  out.setMinutes(out.getMinutes() < 30 ? 0 : 30);
  return out;
}
function clamp(v: number | null | undefined, maxAbs = 20): number | null {
  if (v == null || !isFinite(v as number)) return null;
  const x = Number(v);
  if (!isFinite(x)) return null;
  if (Math.abs(x) > maxAbs) return Math.sign(x) * maxAbs;
  return x;
}

export const AgilePriceChart: React.FC<Props> = ({ prices = [], operations = [] }) => {
  // --- PRICE SERIES --------------------------------------------------
  const priceSeries = useMemo(
    () =>
      (prices ?? [])
        .map((pr) => {
          const ts = pr.timestamp_start ? new Date(pr.timestamp_start) : new Date();
          const safeTs = isNaN(ts.getTime()) ? new Date() : ts;
          return {
            ts: floorToHalfHour(safeTs).toISOString(),
            price: pr.import_price_p_per_kwh,
            isCheap: (pr.import_price_p_per_kwh ?? Infinity) < CHEAP_THRESHOLD_P,
          };
        })
        .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime()),
    [prices]
  );

  // --- OPERATIONS: NET GRID POWER (absolute magnitude) ---------------
  const netBySlot = useMemo(() => {
    const m = new Map<number, number>();
    for (const op of operations ?? []) {
      if (!op?.timestamp) continue;
      const key = floorToHalfHour(new Date(op.timestamp)).getTime();
      const net = Math.abs(clamp(op.grid_power) ?? 0); // absolute net use
      m.set(key, net);
    }
    return m;
  }, [operations]);

  // --- COMBINE DATA -------------------------------------------------
  const data = useMemo(
    () =>
      priceSeries.map((p) => {
        const key = new Date(p.ts).getTime();
        const net = netBySlot.get(key) ?? 0;
        return {
          ts: p.ts,
          price: p.price,
          isCheap: p.isCheap,
          net,
        };
      }),
    [priceSeries, netBySlot]
  );

  const hasData = data.length > 0;

  // --- DETERMINE MULTI-DAY RANGE -----------------------------------
  const firstTs = data[0]?.ts ? new Date(data[0].ts).getTime() : null;
  const lastTs = data[data.length - 1]?.ts ? new Date(data[data.length - 1].ts).getTime() : null;
  const multiDay =
    firstTs != null && lastTs != null && lastTs - firstTs > 24 * 60 * 60 * 1000;

  // --- CHEAP BANDS --------------------------------------------------
  const cheapBands = useMemo(() => {
    if (!hasData) return [] as Array<{ x1: string; x2: string }>;
    const spans: Array<{ x1: string; x2: string }> = [];
    let open: string | null = null;
    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      if (d.isCheap && open == null) open = d.ts;
      const next = data[i + 1];
      if ((!next || !next.isCheap) && open != null) {
        const x1 = open;
        const x2 = data[Math.min(i + 1, data.length - 1)].ts;
        if (x1 !== x2) spans.push({ x1, x2 });
        open = null;
      }
    }
    return spans;
  }, [data, hasData]);

  if (!hasData) {
    return (
      <div className="rounded-2xl border bg-card p-4">
        <div className="mb-2 text-sm text-muted-foreground">
          Agile price (awaiting data…)
        </div>
        <div className="h-64 w-full">
          <ResponsiveContainer>
            <ComposedChart data={[]}>
              <CartesianGrid vertical={false} opacity={0.2} />
              <XAxis dataKey="ts" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // --- RENDER -------------------------------------------------------
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="mb-2 text-sm text-muted-foreground">
        Net grid power vs Dynamic price (Yesterday)
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer>
          <ComposedChart data={data}>
            <CartesianGrid vertical={false} opacity={0.2} />
            <XAxis
              dataKey="ts"
              tickFormatter={(v) => {
                const d = new Date(v);
                return multiDay
                  ? d.toLocaleString([], {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
              }}
              minTickGap={24}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={(v) => `${v.toFixed(1)} kW`}
              domain={[0, "auto"]}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v) => `${v.toFixed(1)} p`}
            />

            {cheapBands.map((b, i) => (
              <ReferenceArea
                key={`cheap-${i}`}
                x1={b.x1}
                x2={b.x2}
                y1={-999}
                y2={999}
                ifOverflow="extendDomain"
                strokeOpacity={0}
                fill="rgba(34,197,94,0.12)"
              />
            ))}

            {/* Net grid power */}
            <Line
              yAxisId="left"
              type="stepAfter"
              dataKey="net"
              name="Net Grid Power (kW)"
              stroke="#3b82f6"
              strokeWidth={1.8}
              dot={false}
            />

            {/* Agile price */}
            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="price"
              name="Agile Price (p/kWh)"
              stroke="#ef4444"
              strokeWidth={1.5}
              dot={false}
            />

            <Tooltip
              formatter={(val: any, name: string) => {
                if (name.includes("Price"))
                  return [`${Number(val).toFixed(2)} p/kWh`, "Price"];
                return [`${Number(val).toFixed(2)} kW`, name];
              }}
              labelFormatter={(v) =>
                new Date(v).toLocaleString([], {
                  weekday: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                  day: "2-digit",
                  month: "short",
                })
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
